import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Play, ArrowRight, FileVideo, X } from "lucide-react";
import axios from "axios";
import {
    Dialog,
    DialogTitle,
    DialogDescription,
    DialogPortal,
    DialogOverlay,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import useSeo from "@/lib/useSeo";

const API = "/api";

const CATEGORY_LABELS_FALLBACK = [
    { slug: "business-insurance", label: "Business Insurance" },
    { slug: "cyber-insurance", label: "Cyber Insurance" },
    { slug: "commercial-lines", label: "Commercial Lines & Specialty" },
    { slug: "weather-property", label: "Weather & Property Risk" },
    { slug: "personal-insurance", label: "Personal Insurance" },
    { slug: "life-health", label: "Life & Health" },
    { slug: "agency-updates", label: "Agency Updates" },
];

function buildStreamUrl(video) {
    // Prefer a same-origin public URL when set (used for demo/test videos
    // hosted in /app/frontend/public/videos/). Otherwise fall back to the
    // Dropbox-proxied stream endpoint, which only succeeds when the admin
    // has uploaded an MP4 + Dropbox creds are configured.
    if (video?.public_url) {
        return video.public_url.startsWith("http")
            ? video.public_url
            : video.public_url;
    }
    if (video?.id && video?.dropbox_path) {
        return `${API}/videos/${video.id}/stream`;
    }
    return null;
}

function VideoCard({ video, onPlay }) {
    const cdnThumbnail = video.thumbnail_url
        ? video.thumbnail_url.startsWith("http")
            ? video.thumbnail_url
            : `${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`
        : null;
    const streamUrl = buildStreamUrl(video);
    const playable = !!streamUrl;
    const isComingSoon =
        !playable && (video.status === "coming_soon" || !video.dropbox_path);
    return (
        <article
            data-testid={`video-card-${video.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] transition-all hover:border-[rgba(212,175,55,0.5)] hover:shadow-[0_30px_60px_-30px_rgba(212,175,55,0.35)]"
        >
            <button
                type="button"
                data-testid={`video-thumb-${video.slug}`}
                onClick={() => playable && onPlay(video)}
                aria-label={playable ? `Play ${video.title}` : video.title}
                disabled={!playable}
                className="relative block aspect-video overflow-hidden bg-[#0d1117] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold disabled:cursor-default"
            >
                {cdnThumbnail ? (
                    <img
                        src={cdnThumbnail}
                        alt={`${video.category_label} — ${video.title}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        <FileVideo
                            aria-hidden="true"
                            className="h-10 w-10 text-brand-gold2/60"
                        />
                    </div>
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-bg/85 via-brand-bg/10 to-transparent" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span
                        aria-hidden="true"
                        className={`flex h-16 w-16 items-center justify-center rounded-full border bg-[#0b0e13]/70 backdrop-blur-md transition-all sm:h-20 sm:w-20 ${
                            playable
                                ? "border-brand-gold/60 group-hover:h-20 group-hover:w-20 group-hover:border-brand-gold group-hover:bg-brand-gold/15"
                                : "border-brand-gold/30"
                        }`}
                    >
                        <Play
                            className={`ml-1 h-7 w-7 sm:h-9 sm:w-9 ${
                                playable
                                    ? "fill-brand-gold2 text-brand-gold2 group-hover:fill-brand-gold group-hover:text-brand-gold"
                                    : "fill-brand-gold2/40 text-brand-gold2/40"
                            }`}
                            strokeWidth={1}
                        />
                    </span>
                </div>
                {isComingSoon && (
                    <span className="absolute right-4 top-4 rounded-full border border-brand-gold/60 bg-[#0b0e13]/85 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-brand-gold2 backdrop-blur">
                        Coming Soon
                    </span>
                )}
                {playable && (
                    <span className="absolute right-4 top-4 rounded-full border border-emerald-400/60 bg-emerald-500/15 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-emerald-200 backdrop-blur">
                        Watch Now
                    </span>
                )}
                {video.featured && (
                    <span className="absolute left-4 top-4 rounded-full border border-brand-gold/60 bg-brand-gold/15 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-brand-gold2 backdrop-blur">
                        Featured
                    </span>
                )}
            </button>

            <div className="flex grow flex-col p-6 md:p-7">
                <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                    {video.category_label}
                </p>
                <h2 className="mt-2 font-serif text-2xl leading-tight text-brand-ivory">
                    {video.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                    {video.description}
                </p>
                {isComingSoon && (
                    <p
                        data-testid={`video-coming-soon-${video.slug}`}
                        className="mt-4 rounded-md border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2 text-xs text-brand-ivory/80"
                    >
                        Video coming soon. Endurance Casualty will provide the
                        MP4 educational video after page structure approval.
                    </p>
                )}
                <div className="mt-5 grow" />
                <div className="flex flex-wrap items-center gap-3">
                    {playable && (
                        <button
                            type="button"
                            data-testid={`video-watch-${video.slug}`}
                            onClick={() => onPlay(video)}
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg"
                        >
                            <Play
                                aria-hidden="true"
                                className="h-3 w-3"
                                strokeWidth={2}
                            />
                            Watch video
                        </button>
                    )}
                    <Link
                        to={video.cta_link || "/contact"}
                        data-testid={`video-cta-${video.slug}`}
                        className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg"
                    >
                        {video.cta_label || "Speak With an Agent"}
                        <ArrowRight aria-hidden="true" className="h-3 w-3" />
                    </Link>
                </div>
            </div>
        </article>
    );
}

function VideoLightbox({ video, onClose }) {
    const videoRef = useRef(null);
    // Reset playback whenever the modal opens with a new video.
    useEffect(() => {
        if (videoRef.current) {
            try {
                videoRef.current.currentTime = 0;
                // Audio is opt-in: user clicked a play button, so unmute and
                // let the native controls handle the rest (most browsers
                // require a user gesture for audible playback — which we
                // already have).
                videoRef.current.muted = false;
                const playPromise = videoRef.current.play();
                if (playPromise && playPromise.catch) {
                    playPromise.catch(() => {
                        /* user can press the visible play button */
                    });
                }
            } catch (_) {
                /* ignore */
            }
        }
        return () => {
            if (videoRef.current) {
                try {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                } catch (_) {
                    /* ignore */
                }
            }
        };
    }, [video?.id]);

    const streamUrl = buildStreamUrl(video);
    const open = !!video;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogPortal>
                <DialogOverlay className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm sm:p-6 md:p-10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0">
                    <DialogPrimitive.Content
                        data-testid="library-video-modal"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        className="relative z-10 mx-auto w-full max-w-5xl border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)] duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 sm:rounded-2xl"
                    >
                        <DialogTitle className="sr-only">
                            {video?.title || "Video"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            {video?.description || ""}
                        </DialogDescription>
                        <div className="relative w-full overflow-hidden rounded-t-2xl bg-black">
                            <div className="relative aspect-video w-full">
                                {streamUrl ? (
                                    <video
                                        ref={videoRef}
                                        data-testid="library-video-player"
                                        src={streamUrl}
                                        poster={
                                            video?.thumbnail_url
                                                ? video.thumbnail_url.startsWith(
                                                      "http"
                                                  )
                                                    ? video.thumbnail_url
                                                    : video.thumbnail_url
                                                : undefined
                                        }
                                        controls
                                        controlsList="nodownload"
                                        playsInline
                                        preload="metadata"
                                        className="absolute inset-0 h-full w-full object-contain"
                                    >
                                        Your browser does not support embedded
                                        video. You can still call us at{" "}
                                        {COMPANY.phone}.
                                    </video>
                                ) : null}
                            </div>
                            <DialogPrimitive.Close
                                aria-label="Close video"
                                data-testid="library-video-close"
                                className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/50 bg-[#0b0e13]/85 text-brand-gold2 backdrop-blur transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                            >
                                <X className="h-5 w-5" />
                            </DialogPrimitive.Close>
                        </div>
                        <div className="flex flex-col items-start justify-between gap-3 border-t border-[rgba(212,175,55,0.2)] px-5 py-4 sm:flex-row sm:items-center sm:px-6">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                                    {video?.category_label}
                                </p>
                                <p className="mt-1 truncate font-serif text-lg text-brand-ivory">
                                    {video?.title}
                                </p>
                            </div>
                            {video?.cta_link && (
                                <Link
                                    to={video.cta_link}
                                    onClick={onClose}
                                    className="shrink-0 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg"
                                >
                                    {video.cta_label || "Speak With an Agent"}
                                </Link>
                            )}
                        </div>
                    </DialogPrimitive.Content>
                </DialogOverlay>
            </DialogPortal>
        </Dialog>
    );
}

export default function VideoLibraryPage() {
    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState(CATEGORY_LABELS_FALLBACK);
    const [activeCategory, setActiveCategory] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    // Backwards-compatibility: any link minted earlier with the
    // `?play=<slug>` / `?v=<slug>` query (or `#<slug>` hash) is now silently
    // redirected to the canonical /video/<slug> page so old marketing
    // emails keep working.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const slug =
            params.get("play") ||
            params.get("v") ||
            (location.hash || "").replace(/^#/, "");
        if (slug) {
            navigate(`/video/${slug}`, { replace: true });
        }
    }, [location.search, location.hash, navigate]);

    useSeo({
        title: "Insurance Education Video Library — Plain-English Coverage Explainers",
        description:
            "Short video explainers on business, personal, life, health, cyber, and specialty insurance from licensed Endurance Casualty agents. New episodes weekly.",
        canonicalPath: "/insurance-education-videos",
    });

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const [vRes, cRes] = await Promise.all([
                    axios.get(`${API}/videos`),
                    axios.get(`${API}/videos/categories`),
                ]);
                if (cancelled) return;
                setVideos(Array.isArray(vRes.data) ? vRes.data : []);
                if (Array.isArray(cRes.data) && cRes.data.length > 0) {
                    setCategories(cRes.data);
                }
            } catch (e) {
                if (cancelled) return;
                setError(
                    "The video library couldn't load right now. Please refresh, or call us directly — we're happy to walk through any coverage topic by phone."
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const filteredVideos = useMemo(() => {
        if (activeCategory === "all") return videos;
        return videos.filter((v) => v.category_slug === activeCategory);
    }, [videos, activeCategory]);

    return (
        <main
            data-testid="page-video-library"
            className="relative min-h-screen bg-brand-bg text-brand-ivory"
        >
            <PageHeader />

            {/* Intro */}
            <section className="mx-auto max-w-[1200px] px-6 pt-12 md:px-10 md:pt-16">
                <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                    Education · Plain-English Coverage Topics
                </p>
                <h1 className="mt-4 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl md:text-6xl">
                    Insurance Education{" "}
                    <span className="gold-text">Video Library</span>
                </h1>
                <div className="gold-hairline my-7 w-32" />
                <p className="max-w-3xl text-base leading-relaxed text-brand-ivory/85 md:text-lg">
                    Short, plain-language videos from {COMPANY.shortName}{" "}
                    explaining the coverages our clients ask about most. Each
                    topic links you directly to a licensed agent for a real
                    conversation about your situation.{" "}
                    {DISCLAIMERS.coverageGeneric}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <ContactDialog
                        defaultMessage="I watched a video in the Education Library and would like to follow up."
                        trigger={
                            <button
                                type="button"
                                data-testid="library-cta-speak"
                                className="enc-submit inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm focus-visible:ring-2 focus-visible:ring-brand-gold"
                            >
                                Speak With an Agent
                                <ArrowRight
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                />
                            </button>
                        }
                    />
                    <Link
                        to="/insurance-solutions"
                        data-testid="library-cta-solutions"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 px-5 py-3 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg"
                    >
                        Browse Insurance Solutions
                        <ArrowRight aria-hidden="true" className="h-3 w-3" />
                    </Link>
                </div>
            </section>

            {/* Category filter */}
            <section
                aria-label="Filter by category"
                className="mx-auto mt-12 max-w-[1200px] px-6 md:px-10"
            >
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        data-testid="library-filter-all"
                        onClick={() => setActiveCategory("all")}
                        className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                            activeCategory === "all"
                                ? "border-brand-gold bg-brand-gold text-brand-bg"
                                : "border-[rgba(212,175,55,0.3)] text-brand-ivory/85 hover:border-brand-gold hover:text-brand-gold2"
                        }`}
                    >
                        All Topics
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            type="button"
                            data-testid={`library-filter-${c.slug}`}
                            onClick={() => setActiveCategory(c.slug)}
                            className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                                activeCategory === c.slug
                                    ? "border-brand-gold bg-brand-gold text-brand-bg"
                                    : "border-[rgba(212,175,55,0.3)] text-brand-ivory/85 hover:border-brand-gold hover:text-brand-gold2"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section
                data-testid="library-grid"
                className="mx-auto max-w-[1200px] px-6 py-12 md:px-10 md:py-16"
            >
                {loading && (
                    <p
                        data-testid="library-loading"
                        className="text-sm text-brand-muted"
                    >
                        Loading the video library…
                    </p>
                )}
                {!loading && error && (
                    <p
                        data-testid="library-error"
                        className="rounded-xl border border-[rgba(212,175,55,0.25)] bg-[#0d1117] p-6 text-sm text-brand-ivory/85"
                    >
                        {error}
                    </p>
                )}
                {!loading && !error && filteredVideos.length === 0 && (
                    <p
                        data-testid="library-empty"
                        className="text-sm text-brand-muted"
                    >
                        No videos in this category yet — check back soon, or
                        switch filters to see other topics.
                    </p>
                )}
                {!loading && !error && filteredVideos.length > 0 && (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredVideos.map((v) => (
                            <VideoCard
                                key={v.id || v.slug}
                                video={v}
                                onPlay={(vid) =>
                                    navigate(`/video/${vid.slug}`)
                                }
                            />
                        ))}
                    </div>
                )}
            </section>
            {/* Educational disclaimer + sensitive-data reminder */}
            <section
                data-testid="library-disclaimer"
                className="mx-auto max-w-[1200px] px-6 pb-20 md:px-10 md:pb-28"
            >
                <div className="rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] p-6 md:p-8">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Important Notice
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-brand-ivory/85">
                        These videos are provided for general educational and
                        marketing purposes only. They are not legal, tax,
                        financial, or coverage advice. Watching a video or
                        submitting a request does not bind coverage, change any
                        policy, or guarantee eligibility, pricing, or
                        availability. Coverage is subject to carrier
                        underwriting, policy terms, exclusions, limits,
                        deductibles, endorsements, and state availability.
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-brand-ivory/85">
                        For your protection, please do not submit Social
                        Security numbers, payment information, medical details,
                        claim documents, or other sensitive information through
                        general website forms. Call us directly at{" "}
                        <a
                            href={`tel:${COMPANY.phoneTel}`}
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            {COMPANY.phone}
                        </a>{" "}
                        to share that information through a secure channel.
                    </p>
                </div>
            </section>

            <FooterBar />
        </main>
    );
}
